import { useState } from "react";

import { AttachFile } from "@mui/icons-material";
import { Button, Stack, Typography } from "@mui/material";

import { DeliveryFilesModal } from "components/DeliveryFilesModal";
import { ShipmentStatusEmptyState } from "components/ShipmentStatusEmptyState";
import { useShipperShipmentPageContext, useSwrFetch } from "hooks";
import {
  formatNumberToDecimal,
  ISOStringToLocalReadableTimestamp,
  shipmentHasBeenDelivered,
} from "utils";
import {
  formatQuotingUnitOfMeasure,
  getBookedQuoteFromQuotes,
  getUnitOfMeasureLabel,
} from "utils/quotes";

const SECTION_STYLE = {
  borderColor: "#cecdcd",
  borderWidth: "1px",
  borderStyle: "solid",
  borderRadius: 2,
  p: ".5rem",
};

export function ShipmentStateDisplay() {
  const { shipment } = useShipperShipmentPageContext();
  const [deliveryFilesOpen, setDeliveryFilesOpen] = useState(false);

  const { data: deliveryFilesData, error: deliveryFilesError } =
    useSwrFetch<DeliveryFilesResponse>({
      url:
        shipment && shipmentHasBeenDelivered(shipment)
          ? `/shipments/${shipment.info.id}/delivery-files`
          : null,
      errorMessage: "Failed to load delivery files.",
    });

  const hasDeliveryFiles =
    (deliveryFilesData?.files.length ?? 0) > 0 || deliveryFilesError !== null;

  let component = (
    <ShipmentStatusEmptyState subtitle="Pickup and delivery updates will appear here once the carrier reports them." />
  );

  if (shipment?.shipment_status === "BOOKING_CANCELLED") {
    if (
      shipment.booking_cancellation !== undefined &&
      shipment.booking_cancellation !== null
    ) {
      component = (
        <Stack direction="column" sx={SECTION_STYLE}>
          <Typography>
            Booking{" "}
            <b>
              <u>cancelled</u>
            </b>{" "}
            by <b>{shipment.booking_cancellation.created_by.user.first_name}</b>{" "}
            <b>{shipment.booking_cancellation.created_by.user.last_name}</b> on{" "}
            {ISOStringToLocalReadableTimestamp(
              shipment.booking_cancellation.created_at,
            )}
          </Typography>
          <Typography>
            <b>{"Reason: "}</b>
            {shipment.booking_cancellation.reason}
          </Typography>
        </Stack>
      );
    }
  }

  if (shipment?.shipment_status === "DELIVERED") {
    if (shipment.delivery !== undefined && shipment.delivery !== null) {
      const bookedQuote = getBookedQuoteFromQuotes(shipment.condensed_quotes);
      const isNotAllIn = shipment.info.quoting_unit_of_measure !== "ALL_IN";
      const hasQuantity = shipment.delivery.unit_of_measure_quantity !== null;
      const quantity = shipment.delivery.unit_of_measure_quantity
        ? parseFloat(
            formatNumberToDecimal(shipment.delivery.unit_of_measure_quantity),
          )
        : 0;
      const rate = bookedQuote?.price ?? 0;
      const finalPrice = (rate * quantity).toLocaleString();

      component = (
        <Stack direction="column" spacing={1} sx={SECTION_STYLE}>
          <Typography>
            Shipment marked as{" "}
            <b>
              <u>delivered</u>
            </b>{" "}
            on{" "}
            {ISOStringToLocalReadableTimestamp(shipment.delivery.delivered_at)}
          </Typography>
          <Typography>
            Marked delivered by{" "}
            <b>{shipment.delivery.created_by.user.first_name}</b>{" "}
            <b>{shipment.delivery.created_by.user.last_name}</b>
          </Typography>
          {isNotAllIn && hasQuantity && bookedQuote && (
            <Stack>
              <Typography>
                <b>Delivered Quantity: </b>
                {quantity.toLocaleString()}{" "}
                {getUnitOfMeasureLabel(shipment.info.quoting_unit_of_measure)}
              </Typography>
              <Typography>
                <b>Rate: </b>${bookedQuote.price?.toLocaleString()}{" "}
                {bookedQuote.currency}{" "}
                {formatQuotingUnitOfMeasure(
                  shipment.info.quoting_unit_of_measure,
                  false,
                )}
              </Typography>
              <Typography>
                <b>Final Price: </b>${finalPrice} {bookedQuote.currency}
              </Typography>
            </Stack>
          )}
          {shipment.delivery.notes != null &&
            shipment.delivery.notes !== "" && (
              <Typography>
                <b>{"Notes: "}</b>
                {shipment.delivery.notes}
              </Typography>
            )}
          <Typography variant="body2" color="text.secondary">
            Delivered status created on{" "}
            {ISOStringToLocalReadableTimestamp(shipment.delivery.created_at)}
          </Typography>
          {hasDeliveryFiles && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachFile />}
              onClick={() => setDeliveryFilesOpen(true)}
              sx={{ alignSelf: "flex-start", mt: 0.5 }}
            >
              View attachments
            </Button>
          )}
        </Stack>
      );
    }
  }

  if (shipment?.shipment_status === "PICKED_UP") {
    if (shipment.first_pickup !== undefined && shipment.first_pickup !== null) {
      component = (
        <Stack direction="column" spacing={1} sx={SECTION_STYLE}>
          <Typography>
            Shipment marked as{" "}
            <b>
              <u>picked up</u>
            </b>
            <u /> on{" "}
            {ISOStringToLocalReadableTimestamp(
              shipment.first_pickup.picked_up_at,
            )}
          </Typography>
          <Typography>
            Marked picked up by{" "}
            <b>{shipment.first_pickup.created_by.user.first_name}</b>{" "}
            <b>{shipment.first_pickup.created_by.user.last_name}</b>
          </Typography>
          {shipment.first_pickup.notes != null &&
            shipment.first_pickup.notes !== "" && (
              <Typography>
                <b>{"Notes: "}</b>
                {shipment.first_pickup.notes}
              </Typography>
            )}
          <Typography variant="body2" color="text.secondary">
            Picked up status created on{" "}
            {ISOStringToLocalReadableTimestamp(
              shipment.first_pickup.created_at,
            )}
          </Typography>
        </Stack>
      );
    }
  }

  return (
    <Stack mt="1rem">
      {component}
      {shipment && shipmentHasBeenDelivered(shipment) && (
        <DeliveryFilesModal
          open={deliveryFilesOpen}
          onClose={() => setDeliveryFilesOpen(false)}
          shipmentId={shipment.info.id}
          preloadedData={deliveryFilesData}
        />
      )}
    </Stack>
  );
}
